import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, Collapse, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Stack, Typography,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Close as CloseIcon,
  ExpandMore as ExpandIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';

/**
 * La barra roja de arriba y el aviso emergente.
 *
 * Existe porque el monitoreo solo sirve si alguien lo ve. Grafana ya sabe que el servidor se
 * está quedando sin memoria; el problema es que para enterarse hay que abrir Grafana, y para
 * abrir Grafana hay que sospechar antes que algo va mal. Esto trae el aviso a la pantalla que
 * ya está abierta todo el día.
 *
 * Dos niveles a propósito:
 *  - **Barra roja**: siempre que haya algo grave. No interrumpe, pero no se puede no verla.
 *  - **Aviso emergente**: solo la PRIMERA vez que aparece cada alerta. Una ventana que salta
 *    cada 30 segundos por lo mismo se aprende a cerrar sin leer, y entonces deja de avisar.
 */

interface AlertaGrafana {
  id: string;
  titulo: string;
  severidad: string;
  grave: boolean;
  resumen: string;
  objetivo: string | null;
  servidor: string | null;
  desde: string;
}

const CADA_MS = 30000;

// Se recuerdan las alertas que ya interrumpieron, por pestaña. Si el navegador se recarga y
// la alerta sigue viva, vuelve a avisar una vez: eso es correcto, es información nueva para
// quien acaba de llegar.
const yaAvisadas = new Set<string>();

const colorDe = (sev: string) => (sev === 'critical' ? 'error' : sev === 'high' ? 'error' : 'warning');
const etiquetaDe = (sev: string) =>
  sev === 'critical' ? 'CRÍTICA' : sev === 'high' ? 'ALTA' : sev.toUpperCase();

const desdeHace = (iso: string) => {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'ahora mismo';
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  return h < 24 ? `hace ${h} h` : `hace ${Math.round(h / 24)} d`;
};

const AlertasGrafana: React.FC = () => {
  const navigate = useNavigate();
  const [alertas, setAlertas] = useState<AlertaGrafana[]>([]);
  const [desplegado, setDesplegado] = useState(false);
  const [emergente, setEmergente] = useState<AlertaGrafana[] | null>(null);
  const [ocultaHasta, setOcultaHasta] = useState(0);
  const montado = useRef(true);

  const consultar = useCallback(async () => {
    try {
      const r: any = await apiService.getMonitoringResumen();
      if (!montado.current) return;
      // Si el monitoreo no está disponible, la pantalla no dice nada: un Grafana caído no
      // puede llenar de errores rojos la pantalla de quien está facturando.
      if (!r?.disponible) { setAlertas([]); return; }

      const graves: AlertaGrafana[] = (r.alertas || []).filter((a: AlertaGrafana) => a.grave);
      setAlertas(graves);

      const nuevas = graves.filter((a) => !yaAvisadas.has(a.id));
      if (nuevas.length > 0) {
        nuevas.forEach((a) => yaAvisadas.add(a.id));
        setEmergente(nuevas);
      }
    } catch {
      // Callar a propósito: el monitoreo es un extra de esta pantalla, no su función.
    }
  }, []);

  useEffect(() => {
    montado.current = true;
    consultar();
    const t = setInterval(consultar, CADA_MS);
    return () => { montado.current = false; clearInterval(t); };
  }, [consultar]);

  const visibles = alertas.length > 0 && Date.now() > ocultaHasta;

  return (
    <>
      {/* ── La barra ── */}
      <Collapse in={visibles}>
        <Alert
          severity="error"
          variant="filled"
          icon={<WarningIcon />}
          sx={{ borderRadius: 0, alignItems: 'center' }}
          action={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Button color="inherit" size="small" onClick={() => navigate('/monitoreo')}>
                Ver todo
              </Button>
              <IconButton size="small" color="inherit" onClick={() => setDesplegado(!desplegado)}
                sx={{ transform: desplegado ? 'rotate(180deg)' : 'none', transition: '0.2s' }}>
                <ExpandIcon fontSize="small" />
              </IconButton>
              {/* Se puede silenciar una hora, no para siempre: una alerta que se puede
                  descartar del todo es una alerta que nadie vuelve a mirar. */}
              <IconButton size="small" color="inherit" title="Ocultar por una hora"
                onClick={() => setOcultaHasta(Date.now() + 3600000)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
        >
          <Typography sx={{ fontWeight: 700 }}>
            {alertas.length === 1
              ? alertas[0].titulo
              : `${alertas.length} alertas activas`}
            {alertas[0]?.objetivo && alertas.length === 1 ? ` — ${alertas[0].objetivo}` : ''}
          </Typography>

          <Collapse in={desplegado}>
            <Box sx={{ mt: 1 }}>
              {alertas.map((a) => (
                <Box key={a.id} sx={{ mb: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={etiquetaDe(a.severidad)} size="small"
                      sx={{ bgcolor: 'rgba(0,0,0,.25)', color: '#fff', fontWeight: 700 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.titulo}</Typography>
                    <Typography variant="caption">{desdeHace(a.desde)}</Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>{a.resumen}</Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Alert>
      </Collapse>

      {/* ── El aviso emergente, solo la primera vez de cada alerta ── */}
      <Dialog open={!!emergente} onClose={() => setEmergente(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningIcon />
          {emergente?.length === 1 ? 'Alerta' : `${emergente?.length} alertas nuevas`}
        </DialogTitle>
        <DialogContent>
          {(emergente || []).map((a) => (
            <Alert key={a.id} severity={colorDe(a.severidad) as any} sx={{ mb: 1.5 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>
                {a.titulo}
                <Chip label={etiquetaDe(a.severidad)} size="small" color="error" sx={{ ml: 1 }} />
              </AlertTitle>
              {a.resumen}
              {a.objetivo && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {a.objetivo} · {desdeHace(a.desde)}
                </Typography>
              )}
            </Alert>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEmergente(null)}>Entendido</Button>
          <Button variant="contained" color="error" startIcon={<OpenIcon />}
            onClick={() => { setEmergente(null); navigate('/monitoreo'); }}>
            Ver el monitoreo
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AlertasGrafana;
